import React, { useState } from 'react';

import { Button } from './buttons/Button';
import { Close } from '../assets/Close';
import { InlineSpinner } from '../assets/Spinner';
import { provinceAbbreviations, stateAbbreviations } from '../pages/constants';
import { Address, CountryCode, Customer } from '../pages/types';
import { formatAddress } from '../utils';

interface AddressSelectModalProps {
  customer: Customer;
  originalAddress?: Address;
  preselectedAddress?: Address;
  onConfirm: (address: Address, shouldCreateNewAddress: boolean) => Promise<void>;
  onCancel: () => void;
}

export const AddressSelectModal = ({
  customer,
  originalAddress,
  preselectedAddress,
  onConfirm,
  onCancel,
}: AddressSelectModalProps) => {
  const country = customer.default_country;

  const originalFormatted = originalAddress ? formatAddress(originalAddress) : undefined;

  const otherAddresses = (customer.addresses as Address[])
    .filter((address) => address.type === 'SHIPPING')
    .filter(
      (address) =>
        formatAddress(address).toLocaleLowerCase() !== originalFormatted?.toLocaleLowerCase()
    );

  const preselectedFormatted = preselectedAddress ? formatAddress(preselectedAddress) : undefined;
  // The current selection is a brand new address if it doesn't match the
  // original order address or any of the customer's saved addresses.
  const isSelectedNew =
    preselectedFormatted !== undefined &&
    preselectedFormatted.toLocaleLowerCase() !== originalFormatted?.toLocaleLowerCase() &&
    !otherAddresses.some(
      (address) =>
        formatAddress(address).toLocaleLowerCase() === preselectedFormatted.toLocaleLowerCase()
    );

  // Prefill the new-address form when the current selection is a new address.
  const newAddressPrefill = isSelectedNew ? preselectedAddress : undefined;

  const [showNewAddressForm, setShowNewAddressForm] = useState(isSelectedNew);
  // Formatted value of the currently selected address. Null if new address form is
  // visible.
  const [selectedExistingAddress, setSelectedExistingAddress] = useState<string | undefined>(
    isSelectedNew ? undefined : preselectedFormatted
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // New address form
  const [streetAddress, setStreetAddress] = useState(newAddressPrefill?.address1 ?? '');
  const [aptNumber, setAptNumber] = useState(newAddressPrefill?.address2 ?? '');
  const [city, setCity] = useState(newAddressPrefill?.city ?? '');
  const [state, setState] = useState(newAddressPrefill?.state ?? '');
  const [postalCode, setPostalCode] = useState(newAddressPrefill?.zip ?? '');

  // Edge case: selected is the original order address, but this address isn't in the
  // customer's list of addresses.
  const isSelectedAddressDeleted =
    selectedExistingAddress !== undefined &&
    selectedExistingAddress?.toLocaleLowerCase() === originalFormatted?.toLocaleLowerCase() &&
    !(customer.addresses as Address[])
      .filter((address) => address.type === 'SHIPPING')
      .some(
        (address) =>
          formatAddress(address).toLocaleLowerCase() === selectedExistingAddress.toLocaleLowerCase()
      );

  const isNewAddressValid =
    streetAddress.trim() !== '' && city.trim() !== '' && state !== '' && postalCode.trim() !== '';
  const isFormValid = showNewAddressForm
    ? isNewAddressValid
    : selectedExistingAddress !== undefined;

  const selectSavedAddress = (address: Address) => {
    setShowNewAddressForm(false);
    setSelectedExistingAddress(formatAddress(address));
  };

  // This builds a dummy Address object to store the address fields in the form.
  // The actual Address object will be created and set to primary once the parent
  // modal (ModificationOrderModal) is submitted.
  const buildNewAddress = (): Address => ({
    id: '', // TODO(SWE-6125) Will be populated when address object is created by backend
    account: customer.id,
    address1: streetAddress.trim(),
    address2: aptNumber.trim() || null,
    city: city.trim(),
    state,
    zip: postalCode.trim(),
    country,
    latitude: null,
    longitude: null,
    // This will be set to primary only when the ModificationOrderModal is submitted.
    is_primary: false,
    type: 'SHIPPING',
    shipping_name: null,
  });

  return (
    <div className="fixed inset-0 flex items-center justify-center m-4 z-50 modal">
      <div className="bg-white rounded-lg shadow-md p-6 w-[510px] relative border overflow-y-auto max-h-[90vh]">
        <Close className="absolute top-4 right-6 w-8 h-8 cursor-pointer" onClick={onCancel} />
        <h1 className="font-bold text-center">Select or add new shipping address</h1>
        <div className="flex flex-col my-4">
          {originalAddress && (
            <div className="flex flex-col">
              <strong>Original order address:</strong>
              <label key={originalFormatted} className="flex items-center">
                <input
                  type="radio"
                  name="shippingAddress"
                  value={originalFormatted}
                  className="mr-2 mt-[6px] self-start"
                  checked={
                    selectedExistingAddress?.toLocaleLowerCase() ===
                    originalFormatted?.toLocaleLowerCase()
                  }
                  onChange={() => selectSavedAddress(originalAddress)}
                />
                <span>{originalFormatted}</span>
              </label>
            </div>
          )}
          {otherAddresses.length > 0 && (
            <div className="flex flex-col mt-4">
              <strong>Other saved addresses:</strong>
              {otherAddresses.map((address) => {
                const addressFormatted = formatAddress(address);
                return (
                  <label key={addressFormatted} className="flex items-center">
                    <input
                      type="radio"
                      name="shippingAddress"
                      value={addressFormatted}
                      className="mr-2 mt-[6px] self-start"
                      checked={
                        selectedExistingAddress?.toLocaleLowerCase() ===
                        addressFormatted?.toLocaleLowerCase()
                      }
                      onChange={() => selectSavedAddress(address)}
                    />
                    <span>{addressFormatted}</span>
                  </label>
                );
              })}
            </div>
          )}
          <div className="flex flex-col mt-4">
            <strong>Add new address:</strong>
            <label key="Add new address" className="flex items-center">
              <input
                type="radio"
                name="shippingAddress"
                value="Add new address"
                className="mr-2 mt-[6px] self-start"
                checked={showNewAddressForm}
                onChange={() => {
                  setShowNewAddressForm(true);
                  setSelectedExistingAddress(undefined);
                }}
              />
              <span>Add new address</span>
            </label>
            {showNewAddressForm && (
              <div className="flex flex-col gap-2 mt-2 pl-[22px] pr-[17px]">
                <input
                  type="text"
                  placeholder="Street address"
                  className="border rounded px-2 py-1 w-full"
                  value={streetAddress}
                  autoComplete="off"
                  onChange={(e) => setStreetAddress(e.target.value)}
                />
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Apt #"
                    className="border rounded px-2 py-1 w-1/2"
                    value={aptNumber}
                    autoComplete="off"
                    onChange={(e) => setAptNumber(e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="City"
                    className="border rounded px-2 py-1 w-1/2"
                    value={city}
                    autoComplete="off"
                    onChange={(e) => setCity(e.target.value)}
                  />
                </div>
                <div className="flex gap-2">
                  <select
                    className="border rounded px-2 py-1 w-1/2"
                    value={state}
                    autoComplete="off"
                    onChange={(e) => setState(e.target.value)}
                  >
                    <option value="" disabled>
                      {country === CountryCode.CA ? 'Province' : 'State'}
                    </option>
                    {(country === CountryCode.CA ? provinceAbbreviations : stateAbbreviations).map(
                      (s) => (
                        <option key={s.short} value={s.short}>
                          {s.short}
                        </option>
                      )
                    )}
                  </select>
                  <input
                    type="text"
                    placeholder={country === CountryCode.US ? 'Zip' : 'Postal Code'}
                    className="border rounded px-2 py-1 w-1/2"
                    value={postalCode}
                    autoComplete="off"
                    onChange={(e) => setPostalCode(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-center mb-4">
          <Button
            isDisabled={!isFormValid || isSubmitting}
            label={isSubmitting ? <InlineSpinner /> : 'Confirm'}
            variant={isFormValid ? 'primary' : 'secondary'}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                if (showNewAddressForm) {
                  if (isNewAddressValid) {
                    await onConfirm(buildNewAddress(), true);
                  }
                } else {
                  const selectedAddress =
                    originalAddress &&
                    formatAddress(originalAddress).toLocaleLowerCase() ===
                      selectedExistingAddress?.toLocaleLowerCase()
                      ? originalAddress
                      : otherAddresses.find(
                          (address) =>
                            formatAddress(address).toLocaleLowerCase() ===
                            selectedExistingAddress?.toLocaleLowerCase()
                        );
                  if (selectedAddress) {
                    // Create new address only if selected address was deleted
                    await onConfirm(selectedAddress, isSelectedAddressDeleted);
                  }
                }
              } finally {
                setIsSubmitting(false);
              }
            }}
          />
        </div>
        {(selectedExistingAddress || showNewAddressForm) && (
          <p className="text-start">
            Please note that this address will be set as the customer’s new
            <br />
            <strong>primary address</strong> for this order and all future orders.
          </p>
        )}
      </div>
    </div>
  );
};
